/**
 * APISAI Guided Workspace — single source of truth.
 *
 * FILOZOFIA:
 *   APISAI = przewodnik krok po kroku, nie panel z chaosem funkcji.
 *   Funkcje odblokowują się sekwencyjnie wraz z postępem.
 *
 * FAZY (phases) w kolejności:
 *   1. profile      — Profil / podstawy obecności
 *   2. page         — Podgląd publicznej strony
 *   3. hero         — Hero / pierwsze wrażenie
 *   4. promo        — Posty i promocja
 *   5. leads        — Zapytania i wiadomości
 *   6. insights     — Wyniki i rekomendacje
 *
 * STANY itema:
 *   - 'active'   — bieżący krok (highlight pomarańczowy — accent APISAI)
 *   - 'done'     — ukończony (check + accent)
 *   - 'next'     — następny krok (subtle glow + "Następny krok")
 *   - 'locked'   — niedostępne ("Odblokujesz po uzupełnieniu profilu")
 *   - 'available'— normalny dostęp (bez highlight)
 *
 * REGUŁA: nigdy nie hardkoduj listy items w komponentach.
 */
/**
 * APISAI Guided Workspace — single source of truth.
 *
 * FILOZOFIA:
 *   APISAI = przewodnik krok po kroku, nie panel z chaosem funkcji.
 *   Funkcje odblokowują się sekwencyjnie wraz z postępem.
 *
 * CONTEXTUAL SIDEBAR (po consolidation #18):
 *   Ten sam shell, ten sam visual language, te same fazy.
 *   ZMIENIA SIĘ tylko domain-specific navigation layer:
 *     - hodowla → Moje psy / Szczenięta / Reproduktory
 *     - biznes  → Oferta / Portfolio / Cennik
 *
 *   User czuje „to system dla mojego biznesu/hodowli", nie „uniwersalny panel".
 *
 * FAZY:
 *   1. profile  · 2. page  · 3. hero  · 4. promo  · 5. leads/reviews  · 6. insights
 *
 * STANY itema:
 *   'active' · 'done' · 'next' · 'locked' · 'available'
 */
import {
  Home, Globe2, Search, Megaphone, MessageCircle, Dog, Smile, Settings,
  Briefcase, ShieldCheck, Wand2, Heart, Award, ListChecks, Camera, BadgePercent,
  FolderOpen, BarChart3, TrendingUp, User as UserIcon, FileText, Send, CalendarDays,
  HelpCircle, PawPrint,
} from 'lucide-react';

/* ─── ADMIN ─── widoczne tylko dla emaili z ADMIN_EMAILS ─── */
export const ADMIN_EMAILS = [
  'ania@apisai.pl',
  'kontakt@apisai.pl',
  'admin@apisai.pl',
  'anialesniak58@gmail.com',
];

export function isAdminUser(user) {
  if (!user?.email) return false;
  return ADMIN_EMAILS.includes(user.email);
}

const adminItems = [
  { key: 'admin-dashboard',  label: 'Admin Dashboard', sub: 'KPI · ruch · konwersje', icon: BarChart3,  to: '/admin/dashboard',     accent: 'admin' },
  { key: 'admin-booster',    label: 'Agencja',         sub: 'Kampanie dla klientów', icon: Megaphone,  to: '/admin/booster',       accent: 'admin' },
  { key: 'admin-funnel',     label: 'Money Flow',      sub: 'Funnel · publish → lead', icon: TrendingUp, to: '/admin/funnel',     accent: 'admin' },
  { key: 'admin-users',      label: 'Użytkownicy',     sub: 'Wszystkie konta',       icon: UserIcon,   to: '/admin/users',         accent: 'admin' },
  { key: 'admin-moderation', label: 'Moderacja',       sub: 'Profile do akceptacji', icon: FileText,   to: '/admin/moderation',    accent: 'admin' },
  { key: 'admin-auto-publish', label: 'Auto-Publish',  sub: 'Debug · pipeline',      icon: Send,       to: '/admin/auto-publish',  accent: 'admin' },
  { key: 'admin-control',    label: 'Narzędzia',       sub: 'Reset · cleanup · DB',  icon: Settings,   to: '/admin/control',       accent: 'admin' },
];

export function buildAdminSidebarItems() {
  return adminItems;
}

/* ─── COMMON (wszyscy userzy) ─── */
/**
 * `startVisible`: czy item POKAZUJE się w START MODE (przed publikacją profilu).
 * START MODE = user nie ukończył jeszcze publikacji (no hero/profile complete).
 * Reszta itemów (Katalog, Promocja, Zapytania, Opinie, Wyniki) jest UKRYTA fizycznie,
 * nie tylko zablokowana — zgodnie z FINALNYM KIERUNKIEM PRODUKTU.
 */
const commonItems = {
  home:     { key: 'home',     label: 'Home',           sub: 'Start',               icon: Home,         to: '/panel',           phase: 0, requires: [], accent: 'home',     startVisible: true },
  hodowla:  { key: 'hodowla',  label: 'Hodowla',        sub: 'Profil, psy, mioty',  icon: Dog,          to: '/panel/strona-hodowli',    phase: 1, requires: [], accent: 'profile',  startVisible: true },
  page:     { key: 'page',     label: 'Profil',         sub: 'Obecność i landing',  icon: Globe2,       to: '/panel/strona',    phase: 1, requires: [], accent: 'profile',  startVisible: true },
  hero:     { key: 'hero',     label: 'Kreator strony', sub: 'Pierwsze wrażenie',   icon: Wand2,        to: '/panel/kreator-top', phase: 1, requires: [], accent: 'hero',     startVisible: true },
  mystuff:  { key: 'mystuff',  label: 'Twoje strony',   sub: 'Profile i oferty',    icon: FolderOpen,   to: '/panel/moje',      phase: 3, requires: [], accent: 'profile',  startVisible: true },
  catalog:  { key: 'catalog',  label: 'Katalog APISAI', sub: 'Publikuj i promuj',   icon: Search,       to: '/panel/katalog',   phase: 3, requires: [], accent: 'catalog',  startVisible: true },
  messages: { key: 'messages', label: 'Zapytania',      sub: 'Twoje wiadomości',    icon: MessageCircle, to: '/panel/leady',    phase: 5, requires: [], accent: 'messages', startVisible: true },
  kalendarz:{ key: 'kalendarz', label: 'Kalendarz ciąży', sub: 'Ciąża suki, terminy', icon: CalendarDays, to: '/panel/kalendarz-ciazy', phase: 0, requires: [], accent: 'profile', startVisible: true },
  promo:    { key: 'promo',    label: 'Promocja',       sub: 'Posty i kampanie',    icon: Megaphone,    to: '/panel/promocja',  phase: 4, requires: ['profile_basics'], accent: 'promo',    startVisible: false },
  opinie:   { key: 'opinie',   label: 'Opinie',         sub: 'Zweryfikowany trust', icon: ShieldCheck,  to: '/panel/opinie',    phase: 5, requires: ['profile_basics'], accent: 'reviews',  startVisible: false },
  visibility:{ key: 'visibility', label: 'Wyniki',      sub: 'Widoczność online · AI', icon: Smile,     to: '/panel/widocznosc', phase: 6, requires: ['profile_basics'], accent: 'results',  startVisible: false },
  settings: { key: 'settings', label: 'Konto',          sub: 'Konto i preferencje', icon: Settings,     to: '/panel/ustawienia', phase: 0, requires: [], accent: 'neutral',  startVisible: true },
  pomoc:    { key: 'pomoc',    label: 'Pomoc',          sub: 'Wsparcie i przewodnik', icon: HelpCircle,  to: '/pomoc',            phase: 0, requires: [], accent: 'neutral',  startVisible: true },
};

/* ─── HODOWLA — domain-specific navigation (MENU_FREEZE: Suki · Reproduktory · Mioty) ─── */
const breederItems = {
  dogs:     { key: 'dogs',     label: 'Suki',           sub: 'Suki hodowlane',       icon: PawPrint, to: '/panel/psy',          phase: 1, requires: [], accent: 'profile' },
  studs:    { key: 'studs',    label: 'Reproduktory',   sub: 'Krycia i linie',       icon: Award,    to: '/panel/reproduktory', phase: 1, requires: [], accent: 'profile' },
  litters:  { key: 'litters',  label: 'Mioty',          sub: 'Mioty i szczenięta',   icon: Heart,    to: '/panel/mioty',        phase: 1, requires: [], accent: 'profile' },
};

/* ─── BIZNES — domain-specific navigation (MENU_FREEZE: Strona firmy · Usługi) ─── */
const businessItems = {
  firmaPage:{ key: 'firmaPage',label: 'Strona firmy',   sub: 'Profil firmy',          icon: Globe2,    to: '/panel/strona-firmy', phase: 1, requires: [], accent: 'profile' },
  offer:    { key: 'offer',    label: 'Usługi',         sub: 'Co oferujesz klientom', icon: Briefcase, to: '/panel/strona-firmy?krok=oferta', phase: 1, requires: [], accent: 'profile' },
  portfolio:{ key: 'portfolio',label: 'Portfolio',      sub: 'Zdjęcia i realizacje',  icon: Camera,    to: '/panel/portfolio', phase: 1, requires: [], accent: 'profile' },
  pricing:  { key: 'pricing',  label: 'Cennik',         sub: 'Pakiety i ceny',        icon: BadgePercent, to: '/panel/cennik', phase: 1, requires: [], accent: 'profile' },
};

/**
 * Buduje sidebar items dostosowane do typu profilu usera.
 *
 * KOLEJNOŚĆ (zgodna z FINALNYM KIERUNKIEM PRODUKTU):
 *   1. Home (orientation)
 *   2. Kreator strony (Hero / Creative Studio — STEP 1)
 *   3. Profil (informacje o firmie/hodowli — STEP 2)
 *   4. Oferta / Psy / Portfolio (domain items — STEP 3)
 *   5. Publikacja (część kreatora — wbudowane)
 *   6. Katalog / Promocja / Zapytania / Opinie / Wyniki — odblokowane po publikacji
 *   7. Ustawienia (zawsze)
 *
 * @param {string} profileType  'hodowla' | 'biznes' (z user.profile_type)
 * @returns Array<sidebar item>
 */
/**
 * Buduje sidebar items dostosowane do typu profilu usera.
 *
 * CZYSTE MENU (po feedbacku: "menu i podlinkowane podstrony są stare"):
 *   - Home, Kreator strony, Moje profile, Ustawienia — modułowe nowe panele.
 *   - Katalog / Promocja / Zapytania / Opinie / Wyniki — locked, do refreshu w P1.
 *   - Domain items (Psy/Mioty/Reproduktory, Oferta/Portfolio/Cennik) — USUNIĘTE z menu,
 *     bo wszystko jest w jednym miejscu w "Moje profile" (`/panel/moje`).
 *   - Profil (legacy `/panel/strona`) — USUNIĘTY z menu (Profil = `Moje profile`).
 *
 * @param {string} profileType  'hodowla' | 'biznes' (z user.profile_type)
 */
/**
 * ETAP 2 (2026-05-31) — SIDEBAR MVP (feature flag).
 *
 * WIDOCZNE (6 modułów MVP, w kolejności):
 *   Home · Hodowla · Twoje strony · Katalog · Zapytania · Kalendarz ciąży  (+ Ustawienia)
 *
 * UKRYTE (feature flag — KOD ZOSTAJE, tylko niewidoczne w UI):
 *   Kreator strony (hero), Profil (page), Promocja (promo), Opinie (opinie),
 *   Wyniki (visibility), Booster.
 * Aby przywrócić moduł — wystarczy dodać go z powrotem do tablicy poniżej.
 *
 * @param {string} profileType  'hodowla' | 'biznes' (z user.profile_type)
 */
export const HIDDEN_MODULES = ['hero', 'page', 'promo', 'opinie', 'visibility', 'booster', 'kalendarz'];

/**
 * MENU_FREEZE (APISAI MASTER COMPLETE v1.0) — jedno menu w całym serwisie.
 *
 * Hodowca: Home · Hodowla(Strona hodowli · Suki · Reproduktory · Mioty) ·
 *          Moje Strony · Katalog · Zapytania · Konto · Pomoc.
 * Firma:   Home · Firma(Strona firmy · Usługi) ·
 *          Moje Strony · Katalog · Zapytania · Konto · Pomoc.
 *
 * `groupLabel` na pierwszym itemie grupy → nad nim renderuje się nagłówek sekcji.
 * Ukryte poza ETAP 1: Promocja, Kalendarz ciąży, Widoczność, Opinie, Booster, Creative/Content Studio.
 *
 * @param {string} profileType  'hodowla' | 'biznes' (z user.profile_type)
 */
export function buildApisaiSidebarItems(profileType) {
  const isBiznes = profileType === 'biznes';
  if (isBiznes) {
    return [
      commonItems.home,
      { ...businessItems.firmaPage, groupLabel: 'Firma' },
      businessItems.offer,
      commonItems.mystuff,
      commonItems.catalog,
      commonItems.messages,
      commonItems.settings,
    ];
  }
  return [
    commonItems.home,
    { ...commonItems.hodowla, groupLabel: 'Hodowla', label: 'Strona hodowli', sub: 'Profil hodowli' },
    breederItems.dogs,
    breederItems.studs,
    breederItems.litters,
    commonItems.mystuff,
    commonItems.catalog,
    commonItems.messages,
    commonItems.settings,
  ];
}

/**
 * START MODE — wszystkie moduły WIDOCZNE zawsze (full menu).
 *
 * Po feedbacku użytkownika: progressive HIDING ukrywało zbyt wiele.
 * Teraz: każdy moduł jest widoczny od początku. Te wymagające wcześniejszego
 * kroku dostają stan 'locked' z subtelną kłódką (computeSidebarStates).
 *
 * Wynik: user widzi pełną mapę produktu, ale rozumie co odblokuje kiedy.
 */
export function filterItemsForStartMode(items, progress) {
  const { hasProfile = false, hasHero = false } = progress || {};
  // Zawsze pełny sidebar. isStartMode = czy user jest jeszcze w fazie onboardingu
  // (używane do delikatnych hint-ów typu "Następny krok").
  const stage = hasHero && hasProfile ? 3 : (hasHero ? 1 : 0);
  return { items, isStartMode: !(hasHero && hasProfile), stage };
}

/**
 * Auto-detect klucza aktywnego itema z path-a.
 */
export function detectActiveSidebarKey(pathname) {
  if (!pathname) return 'home';
  if (pathname === '/panel') return 'home';
  if (pathname.startsWith('/pomoc') || pathname.startsWith('/panel/pomoc')) return 'pomoc';
  // Hodowla domain (MENU_FREEZE: osobne pozycje) — sprawdzane PRZED grupą "Strona hodowli"
  if (pathname.startsWith('/panel/psy') || pathname.startsWith('/panel/suki')) return 'dogs';
  if (pathname.startsWith('/panel/mioty') || pathname.startsWith('/wizard/miot')) return 'litters';
  if (pathname.startsWith('/panel/reproduktory') || pathname.startsWith('/wizard/reproduktor')) return 'studs';
  // Biznes domain
  if (pathname.startsWith('/panel/strona-firmy')) return 'firmaPage';
  if (pathname.startsWith('/panel/oferta') || pathname.startsWith('/wizard/firma')) return 'offer';
  if (pathname.startsWith('/panel/portfolio')) return 'portfolio';
  if (pathname.startsWith('/panel/cennik')) return 'pricing';
  // Strona hodowli / firmy (grupa)
  if (pathname.startsWith('/panel/strona') || pathname.startsWith('/panel/profil') ||
      pathname.startsWith('/wizard/hodowla')) return profileTypeAwareHodowla(pathname);
  if (pathname.startsWith('/panel/kreator-top')) return 'hero';
  if (pathname.startsWith('/panel/moje')) return 'mystuff';
  if (pathname.startsWith('/panel/katalog')) return 'catalog';
  if (pathname.startsWith('/panel/kalendarz-ciazy')) return 'kalendarz';
  if (pathname.startsWith('/panel/promocja') || pathname.startsWith('/panel/publikacja')) return 'promo';
  if (pathname.startsWith('/panel/leady') || pathname.startsWith('/panel/wiadomosci') || pathname.startsWith('/panel/zapytania')) return 'messages';
  if (pathname.startsWith('/panel/opinie')) return 'opinie';
  // Admin
  if (pathname.startsWith('/admin/dashboard')) return 'admin-dashboard';
  if (pathname.startsWith('/admin/booster')) return 'admin-booster';
  if (pathname.startsWith('/admin/funnel')) return 'admin-funnel';
  if (pathname.startsWith('/admin/users')) return 'admin-users';
  if (pathname.startsWith('/admin/moderation')) return 'admin-moderation';
  if (pathname.startsWith('/admin/auto-publish')) return 'admin-auto-publish';
  if (pathname.startsWith('/admin/control') || pathname === '/admin') return 'admin-control';
  // Common
  if (pathname.startsWith('/panel/widocznosc')) return 'visibility';
  if (pathname.startsWith('/panel/ustawienia')) return 'settings';
  return 'home';
}

/** Strona hodowli (hodowca) vs Strona firmy (biznes) — oba korzystają z klucza 'hodowla'/'firmaPage'. */
function profileTypeAwareHodowla(pathname) {
  if (pathname.startsWith('/panel/strona') || pathname.startsWith('/wizard/firma')) {
    // /panel/strona obsługuje obie role — domyślnie traktuj jak "Strona hodowli".
    return 'hodowla';
  }
  return 'hodowla';
}

/**
 * Wylicz stan każdego itema na podstawie postępu usera.
 *
 * @param {Object} progress  z useGuidedProgress() (np. { hasProfile, hasHero, hasPosts, hasLeads })
 * @returns {Object} mapa { itemKey: { state: 'active'|'done'|'next'|'locked'|'available', lockReason?: string } }
 *
 * Logika:
 *   1. Jeśli wszystko spełnione → 'available'
 *   2. Pierwsza niespełniona faza po 'home' → 'next'
 *   3. Itemy z requires które nie są spełnione → 'locked'
 *   4. Itemy z fazą <= aktualnie odblokowanej → 'available' lub 'done'
 */
export function computeSidebarStates(items, progress, activeKey) {
  const { hasProfile = false, hasHero = false, hasPosts = false, hasLeads = false } = progress || {};

  // Map kluczy fazowych do warunków
  const phaseMet = {
    profile_basics: hasProfile,
    hero_published: hasHero,
    posts_active: hasPosts,
    leads_received: hasLeads,
  };

  // Phase-specific lock copy (zgodnie z UI_SPEC #4)
  const lockCopy = {
    hero: 'Po uzupełnieniu profilu',
    catalog: 'Po publikacji profilu',
    promo: 'Po publikacji profilu',
    messages: 'Po pierwszych zapytaniach',
    opinie: 'Po pierwszych klientach',
    visibility: 'Po publikacji profilu',
  };

  const states = {};
  let nextAssigned = false;

  for (const item of items) {
    // Aktywny zawsze winne (override innych stanów)
    if (item.key === activeKey) {
      states[item.key] = { state: 'active' };
      continue;
    }

    // Check requires
    const lockedBy = (item.requires || []).find((r) => !phaseMet[r]);
    if (lockedBy && item.phase > 0) {
      states[item.key] = {
        state: 'locked',
        lockReason: lockCopy[item.key] || 'Odblokujesz po uzupełnieniu profilu',
      };
      continue;
    }

    // Logika done/next/available
    if (item.key === 'page' && hasProfile) {
      states[item.key] = { state: 'done' };
    } else if (item.key === 'page' && !hasProfile) {
      states[item.key] = { state: 'next', hint: 'Zacznij tutaj' };
      nextAssigned = true;
    } else if (item.key === 'hero' && hasHero) {
      states[item.key] = { state: 'done' };
    } else if (item.key === 'hero' && hasProfile && !hasHero && !nextAssigned) {
      states[item.key] = { state: 'next', hint: 'Następny krok' };
      nextAssigned = true;
    } else if (item.key === 'promo' && hasPosts) {
      states[item.key] = { state: 'done' };
    } else if (item.key === 'promo' && hasHero && !hasPosts && !nextAssigned) {
      states[item.key] = { state: 'next', hint: 'Czas zacząć promować' };
      nextAssigned = true;
    } else if (item.key === 'messages' && hasLeads) {
      states[item.key] = { state: 'done' };
    } else {
      states[item.key] = { state: 'available' };
    }
  }

  return states;
}
